<template>
  <section class="co-page detail">
    <router-link :to="{ name: 'coreanos' }" class="back"><b-icon icon="arrow-left" size="is-small" />Pedidos</router-link>

    <div v-if="loading" class="co-card loading"><b-loading :is-full-page="false" :active="true" /></div>
    <p v-else-if="!request" class="co-card empty">Pedido não encontrado.</p>

    <template v-else>
      <!-- 1. Which tour: when, where, status. Secondary actions in the ⋯ menu -->
      <header class="head">
        <div class="head-main">
          <div class="head-tags">
            <span class="co-badge" :class="`is-${status.key}`">{{ status.label }}</span>
            <span v-if="request.isTest" class="tag is-test" title="Mensagens só para o grupo de testes">Teste</span>
            <span class="ref">#C-{{ request.shortId }}</span>
          </div>
          <h1 class="co-title">{{ whenLabel(request) }}</h1>
          <ol class="route">
            <li>
              <span class="pin"></span>
              <span><small>Pick-up</small>{{ request.pickup && request.pickup.label }}</span>
            </li>
            <li>
              <span class="pin is-end"></span>
              <span><small>Drop-off</small>{{ request.dropoff ? request.dropoff.label : "Sem drop-off" }}</span>
            </li>
          </ol>
        </div>
        <div class="head-actions">
          <router-link v-if="editable" :to="{ name: 'coreanos-edit', params: { id: request.id } }" class="co-btn is-small">
            <b-icon icon="pen" size="is-small" />Editar
          </router-link>
          <b-dropdown position="is-bottom-left" aria-role="menu">
            <template #trigger>
              <button type="button" class="icon-btn" aria-label="Mais ações"><b-icon icon="ellipsis-h" size="is-small" /></button>
            </template>
            <b-dropdown-item aria-role="menuitem" @click="copy(message)"><b-icon icon="copy" size="is-small" />Copiar texto do anúncio</b-dropdown-item>
            <b-dropdown-item v-if="canResend" aria-role="menuitem" @click="sendAgain"><b-icon icon="paper-plane" size="is-small" />Enviar de novo para o Telegram</b-dropdown-item>
            <template v-if="editable">
              <hr class="dropdown-divider" />
              <b-dropdown-item aria-role="menuitem" class="is-danger-item" @click="cancel"><b-icon icon="times" size="is-small" />Cancelar tour</b-dropdown-item>
            </template>
          </b-dropdown>
        </div>
      </header>

      <dl class="facts">
        <div>
          <dt>Tuk-tuks</dt>
          <dd>{{ request.tuksTotal }}<small v-if="request.tuksSixSeatsRequired"> · {{ request.tuksSixSeatsRequired }} de 6 lugares</small></dd>
        </div>
        <div>
          <dt>Pagamento</dt>
          <dd>{{ request.guidePay == null ? "Sem valor" : `${request.guidePay} € por guia` }}</dd>
        </div>
        <div>
          <dt>Respostas</dt>
          <dd>{{ availableCount }} <small>disponíveis · {{ unavailable.length }} não</small></dd>
        </div>
      </dl>

      <!-- 2. The one thing to do now -->
      <section class="next" :class="`is-${next.tone}`" aria-live="polite">
        <b-icon :icon="next.icon" class="next-icon" />
        <div class="next-text">
          <p class="next-title">{{ next.title }}</p>
          <p v-if="next.text" class="next-sub">{{ next.text }}</p>
        </div>
        <div v-if="next.action || next.link" class="next-actions">
          <router-link v-if="next.link" :to="next.link.to" class="co-btn">{{ next.link.label }}</router-link>
          <button v-if="next.action" type="button" class="co-btn" :class="{ 'is-lime': next.tone === 'action' }" :disabled="!!busy" @click="next.action.run">
            {{ busy === next.action.busy ? next.action.busyLabel : next.action.label }}
          </button>
        </div>
      </section>

      <div class="co-layout">
        <!-- 3. People: the team, who can go, who can't -->
        <section class="co-card people">
          <div class="tabs-bar" role="tablist">
            <button
              v-for="t in tabs"
              :key="t.id"
              type="button"
              role="tab"
              :aria-selected="tab === t.id"
              :class="{ 'is-active': tab === t.id }"
              @click="tab = t.id"
            >
              {{ t.label }}<span class="count">{{ t.count }}</span>
            </button>
          </div>

          <!-- Equipa -->
          <div v-if="tab === 'team'" role="tabpanel">
            <div v-if="canEditTeam && team.length > 1" class="panel-tools">
              <button type="button" class="link" @click="toggleSelecting">{{ selecting ? "Cancelar seleção" : "Selecionar vários" }}</button>
            </div>
            <p v-if="started && team.length" class="panel-note">Marca quem chegou atrasado ou não apareceu. Conta para a avaliação do guia.</p>

            <div
              v-for="g in team"
              :key="g.guideId"
              class="person"
              :class="{ 'is-pending': isLeaving(g), 'is-changed': !!seatsChange(g), 'is-selected': selected.includes(g.guideId) }"
            >
              <label v-if="selecting && !isLeaving(g)" class="pick">
                <input v-model="selected" type="checkbox" :value="g.guideId" />
                <span class="sr-only">Selecionar {{ g.guideName }}</span>
              </label>
              <div class="person-main">
                <p class="person-name">
                  {{ g.guideName }}
                  <span v-if="g.isExternal" class="tag is-ext">Externo</span>
                  <span v-if="g.slot === 'six'" class="tag">lugar de 6</span>
                </p>
                <p class="person-meta">
                  {{ g.seats }} lugares<template v-if="!g.isExternal"> · ★ {{ ratingLabel(g.rating) }}</template> ·
                  <span class="state" :class="reconfirmState(g).cls">{{ reconfirmState(g).label }}</span>
                </p>
                <p v-if="conflictText(g)" class="person-warn"><b-icon icon="exclamation-triangle" size="is-small" />{{ conflictText(g) }}</p>
              </div>

              <!-- After the start: who was late or didn't show up -->
              <label v-if="started && !g.isExternal" class="attendance" :class="`is-${attendanceOf(g)}`">
                <span class="sr-only">Presença de {{ g.guideName }}</span>
                <select :value="attendanceOf(g)" :disabled="!!busy" @change="setAttendance(g, $event.target.value)">
                  <option v-for="o in attendanceOptions" :key="o.id" :value="o.id">{{ o.label }}</option>
                </select>
              </label>
              <div v-else-if="pendingFor(g)" class="person-actions">
                <span class="pending-tag">{{ pendingLabel(pendingFor(g)) }}</span>
                <button type="button" class="link" :disabled="!!busy" @click="undoPending(g)">Desfazer</button>
              </div>
              <div v-else-if="!selecting" class="person-actions">
                <a v-if="g.phone" :href="waLink(g.phone)" target="_blank" rel="noopener" class="icon-btn" :aria-label="`WhatsApp de ${g.guideName}`">
                  <b-icon icon="comment" size="is-small" />
                </a>
                <b-dropdown v-if="canEditTeam" position="is-bottom-left" aria-role="menu">
                  <template #trigger>
                    <button type="button" class="icon-btn" :aria-label="`Ações para ${g.guideName}`"><b-icon icon="ellipsis-v" size="is-small" /></button>
                  </template>
                  <b-dropdown-item aria-role="menuitem" @click="openSeats(g)">
                    <b-icon icon="chair" size="is-small" />Mudar lugares do tuk ({{ g.seats }})
                  </b-dropdown-item>
                  <b-dropdown-item v-if="swapCandidates.length" aria-role="menuitem" @click="swap(g)">
                    <b-icon icon="exchange-alt" size="is-small" />Trocar por outro guia
                  </b-dropdown-item>
                  <b-dropdown-item aria-role="menuitem" class="is-danger-item" @click="removeGuide(g)">
                    <b-icon icon="user-minus" size="is-small" />Remover do tour
                  </b-dropdown-item>
                </b-dropdown>
              </div>
            </div>

            <!-- Open spots: one tap shows who can take them -->
            <button v-for="slot in openSlots" :key="`open-${slot.n}`" type="button" class="slot" :disabled="!canEditTeam" @click="tab = 'available'">
              <span class="slot-icon"><b-icon icon="plus" size="is-small" /></span>
              <span class="slot-text">
                <strong>Lugar livre</strong>
                <span class="small">{{ slot.six ? "Precisa de tuk de 6 lugares" : "4 ou 6 lugares" }}<template v-if="slot.hint"> · {{ slot.hint }}</template></span>
              </span>
              <b-icon v-if="canEditTeam" icon="chevron-right" size="is-small" class="slot-go" />
            </button>
            <p v-if="!team.length && !openSlots.length" class="empty-state">Ninguém na equipa.</p>

            <!-- Someone from outside the group -->
            <div v-if="canEditTeam" class="external">
              <button v-if="!showExternal" type="button" class="link" @click="showExternal = true">
                <b-icon icon="user-plus" size="is-small" />Adicionar guia de fora do grupo
              </button>
              <form v-else class="external-form" novalidate @submit.prevent="addExternal">
                <p class="external-title">Guia de fora do grupo <span class="small">· entra logo num lugar livre</span></p>
                <label class="field-ui"><span>Nome</span><input v-model.trim="external.name" type="text" placeholder="Nome do guia" /></label>
                <label class="field-ui"><span>Telefone <em>(opcional)</em></span><input v-model.trim="external.phone" type="tel" placeholder="+351 9XX XXX XXX" /></label>
                <div class="field-ui">
                  <span>Tuk</span>
                  <div class="seg">
                    <button type="button" :class="{ 'is-on': external.seats === 4 }" @click="external.seats = 4">4 lugares</button>
                    <button type="button" :class="{ 'is-on': external.seats === 6 }" @click="external.seats = 6">6 lugares</button>
                  </div>
                </div>
                <div class="external-actions">
                  <button type="button" class="link" @click="showExternal = false">Cancelar</button>
                  <button type="submit" class="co-btn is-lime is-small" :disabled="busy === 'external'">{{ busy === "external" ? "A adicionar…" : "Adicionar" }}</button>
                </div>
              </form>
            </div>
          </div>

          <!-- Disponíveis: suggestion first, then the waiting list -->
          <div v-else-if="tab === 'available'" role="tabpanel">
            <p class="panel-note">{{ availableNote }}</p>
            <div v-for="w in candidates" :key="w.guideId" class="person">
              <div class="person-main">
                <p class="person-name">
                  {{ w.guideName }}
                  <span v-if="isSuggested(w)" class="tag is-suggested">Sugerido</span>
                  <span v-if="w.suggestion === 'six'" class="tag">lugar de 6</span>
                </p>
                <p class="person-meta">
                  {{ w.seats }} lugares · ★ {{ ratingLabel(w.rating) }} · respondeu {{ stamp(w.respondedAt) }}<template v-if="afterWindow(w)"> (depois da janela)</template>
                </p>
                <p v-if="conflictText(w)" class="person-warn"><b-icon icon="exclamation-triangle" size="is-small" />{{ conflictText(w) }}</p>
              </div>
              <div v-if="pendingFor(w)" class="person-actions">
                <span class="pending-tag">{{ pendingLabel(pendingFor(w)) }}</span>
                <button type="button" class="link" :disabled="!!busy" @click="undoPending(w)">Desfazer</button>
              </div>
              <div v-else class="person-actions">
                <a v-if="w.phone" :href="waLink(w.phone)" target="_blank" rel="noopener" class="icon-btn" :aria-label="`WhatsApp de ${w.guideName}`">
                  <b-icon icon="comment" size="is-small" />
                </a>
                <button v-if="canEditTeam" type="button" class="co-btn is-small" :class="{ 'is-lime': isSuggested(w) }" :disabled="!!busy" @click="assignNow(w)">Aprovar</button>
                <b-dropdown v-if="canEditTeam" position="is-bottom-left" aria-role="menu">
                  <template #trigger>
                    <button type="button" class="icon-btn" :aria-label="`Ações para ${w.guideName}`"><b-icon icon="ellipsis-v" size="is-small" /></button>
                  </template>
                  <b-dropdown-item aria-role="menuitem" @click="openSeats(w)">
                    <b-icon icon="chair" size="is-small" />Mudar lugares do tuk ({{ w.seats }})
                  </b-dropdown-item>
                </b-dropdown>
              </div>
            </div>
            <p v-if="!candidates.length" class="empty-state">Ainda ninguém disponível para este tour.</p>
          </div>

          <!-- Não podem -->
          <div v-else role="tabpanel">
            <div v-for="r in unavailable" :key="r.guideId" class="person is-muted">
              <div class="person-main">
                <p class="person-name">{{ r.guideName }}</p>
                <p class="person-meta">{{ r.swappedOutAt ? "Trocou com outro guia" : "Disse que não pode" }}</p>
              </div>
            </div>
            <p v-if="!unavailable.length" class="empty-state">Ninguém disse que não pode.</p>
          </div>

          <!-- Team changes wait here until "Guardar e avisar" -->
          <div v-if="selected.length || pending.length" class="pending-bar">
            <div v-if="selected.length" class="pending-row">
              <span>{{ plural(selected.length, "guia selecionado", "guias selecionados") }}</span>
              <button type="button" class="co-btn is-small" :disabled="!!busy" @click="removeSelected">Remover selecionados</button>
            </div>
            <div v-if="pending.length" class="pending-row">
              <span>
                <strong>{{ plural(pending.length, "alteração por guardar", "alterações por guardar") }}</strong>
                <span class="small"> · ninguém é avisado até guardares</span>
              </span>
              <div class="pending-actions">
                <button type="button" class="link" :disabled="!!busy" @click="pending = []">Descartar</button>
                <button type="button" class="co-btn is-lime is-small" :disabled="!!busy" @click="savePending">
                  {{ busy === "team" ? "A guardar…" : "Guardar e avisar" }}
                </button>
              </div>
            </div>
          </div>
        </section>

        <aside class="side">
          <!-- Client (admins only) -->
          <section class="co-card side-card">
            <h2 class="side-title"><b-icon icon="lock" size="is-small" />Cliente <span>só admins</span></h2>
            <div class="client">
              <p class="phone">{{ priv.clientWhatsapp || "Sem contacto" }}</p>
              <div v-if="priv.clientWhatsapp" class="client-actions">
                <a :href="waLink(priv.clientWhatsapp)" target="_blank" rel="noopener" class="icon-btn" aria-label="Abrir conversa no WhatsApp">
                  <b-icon icon="comment" size="is-small" />
                </a>
                <button type="button" class="icon-btn" aria-label="Copiar número" @click="copy(priv.clientWhatsapp)">
                  <b-icon icon="copy" size="is-small" />
                </button>
              </div>
            </div>
            <p v-if="priv.internalNotes" class="notes">{{ priv.internalNotes }}</p>
          </section>

          <!-- What happens when -->
          <section class="co-card side-card">
            <h2 class="side-title">Prazos</h2>
            <ol class="timeline">
              <li v-for="step in timeline" :key="step.label" :class="{ 'is-done': step.done }">
                <span class="tl-dot"></span>
                <span class="tl-label">{{ step.label }}</span>
                <span class="tl-when">{{ step.when }}</span>
              </li>
            </ol>
          </section>

          <!-- The announcement (collapsed: rarely needed) -->
          <details class="co-card side-card announce">
            <summary>
              <span class="side-title">Anúncio no Telegram</span>
              <span class="small">{{ announceNote }}</span>
            </summary>
            <p class="co-message">{{ message }}</p>
            <button type="button" class="link" @click="copy(message)">Copiar texto</button>
          </details>

          <section class="co-card side-card">
            <h2 class="side-title">Histórico</h2>
            <ul class="history">
              <li v-for="entry in shownLog" :key="entry.id">
                <p class="entry">{{ entry.message }}</p>
                <p class="small">{{ stamp(entry.at) }} · {{ actorLabel(entry.actor) }}</p>
              </li>
            </ul>
            <p v-if="!log.length" class="small">Sem registos.</p>
            <button v-if="log.length > 5 && !showAllLog" type="button" class="link" @click="showAllLog = true">Ver tudo ({{ log.length }})</button>
          </section>
        </aside>
      </div>
    </template>

    <!-- Seats of a guide's tuk (e.g. they told the admin they now have a 6-seat tuk) -->
    <div v-if="seatsOf" ref="seatsOverlay" class="sheet-overlay" tabindex="-1" @click.self="seatsOf = null" @keydown.esc="seatsOf = null">
      <div class="co-card sheet" role="dialog" aria-modal="true">
        <h2 class="sheet-title">Tuk de {{ seatsOf.guide.guideName }}</h2>
        <p class="small">Agora: {{ seatsOf.guide.seats }} lugares</p>
        <p class="sheet-q">Quantos lugares tem o tuk?</p>
        <div class="seg is-big">
          <button type="button" :class="{ 'is-on': seatsOf.seats === 4 }" @click="seatsOf.seats = 4">4 lugares</button>
          <button type="button" :class="{ 'is-on': seatsOf.seats === 6 }" @click="seatsOf.seats = 6">6 lugares</button>
        </div>
        <p v-if="seatsWarning" class="option-warn mt-small">⚠ {{ seatsWarning }}</p>
        <p class="sheet-note">
          <b-icon icon="info-circle" size="is-small" />Fica na lista de alterações. Ao guardar, {{ seatsOf.guide.assignment === "assigned" ? "o grupo vê a lista atualizada e o guia recebe uma mensagem privada com o tuk novo" : "o guia recebe o estado atualizado no Telegram" }}.
        </p>
        <div class="sheet-actions">
          <button type="button" class="co-btn" @click="seatsOf = null">Voltar</button>
          <button type="button" class="co-btn is-lime" :disabled="seatsOf.seats === seatsOf.guide.seats" @click="confirmSeats">Adicionar à lista</button>
        </div>
      </div>
    </div>

    <!-- Remove / swap: who takes the spot and why the guide leaves -->
    <div v-if="leaving" ref="reasonOverlay" class="sheet-overlay" tabindex="-1" @click.self="leaving = null" @keydown.esc="leaving = null">
      <div class="co-card sheet" role="dialog" aria-modal="true">
        <h2 class="sheet-title">
          {{ leaving.swap ? `Trocar ${leaving.guides[0].guideName}` : leaving.guides.length > 1 ? `Remover ${leaving.guides.length} guias` : `Remover ${leaving.guides[0].guideName}` }}
        </h2>
        <p v-if="leaving.guides.length > 1" class="small">{{ leaving.guides.map((g) => g.guideName).join(", ") }}</p>

        <template v-if="leaving.swap">
          <p class="sheet-q">Quem fica com o lugar?</p>
          <div class="options">
            <button
              v-for="w in swapCandidates"
              :key="w.guideId"
              type="button"
              class="option"
              :class="{ 'is-on': leaving.to === w }"
              @click="leaving.to = w"
            >
              <span class="option-name">{{ w.guideName }}<span v-if="isSuggested(w)" class="tag is-suggested">Sugerido</span></span>
              <span class="small">{{ w.seats }} lugares · ★ {{ ratingLabel(w.rating) }}</span>
              <span v-if="swapProblem(leaving.guides[0], w)" class="option-warn">⚠ {{ swapProblem(leaving.guides[0], w) }}</span>
            </button>
          </div>
        </template>

        <p class="sheet-q">{{ leaving.guides.length > 1 ? "Porque saem?" : `Porque sai ${leaving.guides[0].guideName}?` }}</p>
        <div class="options">
          <label v-for="r in reasons" :key="r.id" class="option is-radio" :class="{ 'is-on': leaving.reason === r.id }">
            <input v-model="leaving.reason" type="radio" :value="r.id" />
            <span class="option-name">{{ r.label }}</span>
            <span v-if="r.hint" class="small">{{ r.hint }}</span>
          </label>
        </div>
        <textarea v-if="leaving.reason === 'other'" v-model.trim="leaving.other" rows="3" placeholder="Escreve o motivo (o guia vai ler)"></textarea>

        <p class="sheet-note"><b-icon icon="info-circle" size="is-small" />Fica na lista de alterações. Os guias e o grupo só são avisados quando carregares em «Guardar e avisar».</p>
        <div class="sheet-actions">
          <button type="button" class="co-btn" @click="leaving = null">Voltar</button>
          <button type="button" class="co-btn is-lime" :disabled="!!busy || (leaving.swap && !leaving.to)" @click="confirmLeaving">Adicionar à lista</button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
// The admin chose someone whose tuk is smaller than the one leaving: allowed, but the server must accept it
const g6 = (guide, to) => Number(to.seats) < Number(guide.seats);

import {
  watchTourRequest,
  getAdminData,
  publishTourRequest,
  sendTourMessage,
  assignGuide,
  approveSuggestion,
  saveTeamChanges,
  markAttendance,
  ratingLabel,
  addExternalGuide,
  cancelTourRequest,
  whenLabel,
  stamp,
  statusOf,
  newTourMessage,
  waLink,
  plural,
  dayLabel,
} from "@/services/coreanos";

export default {
  name: "CoreanosDetail",
  metaInfo() {
    return { title: `${this.request ? whenLabel(this.request) : "Pedido"} | Pedidos Coreanos` };
  },
  // Team changes not saved yet would be lost
  beforeRouteLeave(to, from, next) {
    if (!this.pending.length || window.confirm("Tens alterações à equipa por guardar. Sair sem guardar?")) next();
    else next(false);
  },
  data() {
    return {
      tab: "team", // team | available | unavailable
      selecting: false, // "Selecionar vários" on the team
      showExternal: false,
      leaving: null, // { guides, swap, to, reason, other }: remove / swap sheet
      seatsOf: null, // { guide, seats }: change the seats of a guide's tuk
      selected: [], // guideIds ticked on the team (remove several at once)
      // Changes not saved yet: [{ guideId, guideName, seats, to, reason, other }]
      // (removal / swap) or [{ guideId, guideName, seats, newSeats }] (tuk seats)
      pending: [],
      reasons: [
        { id: "fewer_tuks", label: "O número de tuk-tuks foi reduzido", hint: "Não conta contra o guia" },
        { id: "asked", label: "Pediu para sair do tour", hint: "Conta como desistência na avaliação" },
        { id: "other", label: "Outro motivo" },
      ],
      // Presença: each late option stands for its range of minutes (utils/coreanosScore.js)
      attendanceOptions: [
        { id: "ok", label: "Chegou a horas", status: "ok", minutes: null },
        { id: "late10", label: "Atrasado 5–15 min", status: "late", minutes: 10 },
        { id: "late20", label: "Atrasado 15–30 min", status: "late", minutes: 20 },
        { id: "late45", label: "Atrasado +30 min", status: "late", minutes: 45 },
        { id: "no_show", label: "Não apareceu", status: "no_show", minutes: null },
      ],
      external: { name: "", phone: "", seats: 4 },
      request: null,
      priv: {},
      log: [],
      responses: [],
      loading: true,
      busy: "",
      showAllLog: false,
      now: Date.now(),
    };
  },
  computed: {
    status() {
      return statusOf(this.request, this.now);
    },
    editable() {
      return !["cancelled", "done"].includes(this.status.key);
    },
    started() {
      return !!this.request && new Date(this.request.startAt).getTime() <= this.now;
    },
    teamEditable() {
      return this.status.key !== "cancelled";
    },
    // Approve / swap / remove: before the start (afterwards only attendance)
    canEditTeam() {
      return !!this.request && this.teamEditable && !this.started;
    },
    canResend() {
      return !["draft", "cancelled"].includes(this.request.status) && !this.started;
    },
    assigned() {
      return (this.request.counts && this.request.counts.assigned) || 0;
    },
    openCount() {
      return Math.max(0, this.request.tuksTotal - this.team.length);
    },
    ranked() {
      return this.responses
        .filter((r) => r.availability === "available")
        .sort((a, b) => !!b.isExternal - !!a.isExternal || (b.rating || 0) - (a.rating || 0) || String(a.respondedAt).localeCompare(String(b.respondedAt)));
    },
    // 6-seat slots first, then the rest, each in rank order
    team() {
      const team = this.ranked.filter((r) => r.assignment === "assigned");
      return [...team.filter((r) => r.slot === "six"), ...team.filter((r) => r.slot !== "six")];
    },
    // Not on the team: suggested by the rules first, then the waiting list
    candidates() {
      const all = this.ranked.filter((r) => r.assignment !== "assigned" && r.assignment !== "removed");
      return [...all.filter((r) => this.isSuggested(r)), ...all.filter((r) => !this.isSuggested(r))];
    },
    // For a swap: available and not already chosen for another pending swap
    swapCandidates() {
      const taken = new Set(this.pending.filter((p) => p.to).map((p) => p.to.guideId));
      return this.candidates.filter((w) => !taken.has(w.guideId));
    },
    suggested() {
      return this.candidates.filter((r) => this.isSuggested(r));
    },
    availableCount() {
      return this.ranked.length;
    },
    unavailable() {
      return this.responses.filter((r) => r.availability !== "available");
    },
    notReconfirmed() {
      return this.team.filter((g) => g.reconfirm && g.reconfirm.status === "not_reconfirmed").length;
    },
    tabs() {
      return [
        { id: "team", label: "Equipa", count: `${this.team.length}/${this.request.tuksTotal}` },
        { id: "available", label: "Disponíveis", count: this.candidates.length },
        { id: "unavailable", label: "Não podem", count: this.unavailable.length },
      ];
    },
    openSlots() {
      const openSix = Math.max(0, (this.request.tuksSixSeatsRequired || 0) - this.team.filter((g) => g.slot === "six").length);
      const sixWaiting = this.candidates.filter((w) => w.seats === 6).length;
      return Array.from({ length: this.openCount }, (_, i) => ({
        n: this.team.length + i + 1,
        six: i < openSix,
        hint: i < openSix ? (sixWaiting ? `${plural(sixWaiting, "disponível", "disponíveis")} com 6 lugares` : "ninguém disponível com 6 lugares") : this.candidates.length ? plural(this.candidates.length, "disponível", "disponíveis") : "",
      }));
    },
    seatsWarning() {
      const s = this.seatsOf;
      if (!s || s.seats !== 4 || s.guide.slot !== "six") return "";
      return "Está num lugar que precisa de tuk de 6 lugares. Esse lugar fica por preencher.";
    },
    availableNote() {
      if (!this.candidates.length) return "";
      if (!this.suggested.length) return "Por ordem de avaliação e de resposta. Podes aprovar quem quiseres se houver lugar.";
      return "«Sugerido» = os melhores para os lugares livres (trabalhadores primeiro, depois avaliação e quem teve menos tours). Os outros ficam em lista de espera.";
    },
    // What the admin should do now: one message, at most one button
    next() {
      const r = this.request;
      const open = this.openCount;
      if (r.status === "cancelled") return { tone: "muted", icon: "ban", title: "Tour cancelado", text: r.sharedAt ? "Os guias foram avisados." : "Nunca foi anunciado: ninguém foi avisado." };
      if (r.status === "draft" && r.publishAt) {
        return {
          tone: "info",
          icon: "clock",
          title: `Agendado · anuncia ${this.clock(r.publishAt, true)}`,
          text: "Até lá é um rascunho: podes alterar ou cancelar e ninguém é avisado. A essa hora vai para o grupo do Telegram e começam 30 min para respostas.",
          link: { label: "Mudar agendamento", to: { name: "coreanos-edit", params: { id: r.id }, query: { agendar: 1 } } },
          action: { label: "Publicar já", busy: "publish", busyLabel: "A publicar…", run: this.publish },
        };
      }
      if (r.status === "draft") {
        return {
          tone: "action",
          icon: "paper-plane",
          title: "Rascunho · ainda não publicado",
          text: "Ao publicar, o anúncio vai para o grupo do Telegram e começam 30 min para os guias responderem.",
          link: { label: "Agendar", to: { name: "coreanos-edit", params: { id: r.id }, query: { agendar: 1 } } },
          action: { label: "Publicar", busy: "publish", busyLabel: "A publicar…", run: this.publish },
        };
      }
      if (this.started) {
        return {
          tone: "muted",
          icon: "flag-checkered",
          title: this.status.key === "done" ? "Tour concluído" : "Tour a decorrer",
          text: this.team.length ? "Marca na equipa quem chegou atrasado ou não apareceu." : "",
        };
      }
      if (!r.sharedAt) {
        return {
          tone: "warn",
          icon: "exclamation-triangle",
          title: "O anúncio não chegou ao Telegram",
          text: "Os guias ainda não sabem deste tour.",
          action: { label: "Enviar de novo", busy: "send", busyLabel: "A enviar…", run: this.sendAgain },
        };
      }
      const approve = this.suggested.length && { label: `Aprovar sugestão (${this.suggested.length})`, busy: "assign", busyLabel: "A aprovar…", run: this.approveAll };
      if (!r.selectionDone && open) {
        return {
          tone: approve ? "action" : "info",
          icon: "hourglass-half",
          title: `A receber respostas · fecha às ${this.clock(r.selectionClosesAt)}`,
          text: `${plural(this.availableCount, "disponível", "disponíveis")} para ${plural(open, "lugar", "lugares")}. Podes aprovar já ou esperar pelo fim da janela.`,
          action: approve || null,
        };
      }
      if (open && approve) {
        return {
          tone: "action",
          icon: "user-check",
          title: `${open === 1 ? "Falta 1 guia" : `Faltam ${open} guias`}`,
          text: `A sugestão tem ${plural(this.suggested.length, "guia", "guias")} para os lugares livres.`,
          action: approve,
        };
      }
      if (open) {
        return {
          tone: "warn",
          icon: "user-clock",
          title: `${open === 1 ? "Falta 1 guia" : `Faltam ${open} guias`}`,
          text: "Ainda ninguém disponível. O grupo volta a ser avisado de hora a hora; também podes adicionar um guia de fora.",
        };
      }
      if (this.notReconfirmed) {
        return { tone: "warn", icon: "phone", title: `${plural(this.notReconfirmed, "guia não reconfirmou", "guias não reconfirmaram")} a nova hora`, text: "Liga-lhes para confirmar." };
      }
      return { tone: "ok", icon: "check-circle", title: "Equipa completa", text: "Os guias foram avisados no Telegram. Na véspera às 17:00 recebem o lembrete." };
    },
    // Announcement → answers → reminder → lock → start
    timeline() {
      const r = this.request;
      const start = new Date(r.startAt).getTime();
      return [
        { label: "Anúncio no grupo", when: r.sharedAt ? this.clock(r.sharedAt, true) : r.publishAt ? `agendado ${this.clock(r.publishAt, true)}` : r.status === "draft" ? "ao publicar" : "não enviado", done: !!r.sharedAt },
        {
          label: "Respostas (30 min)",
          when: r.selectionClosesAt ? `${r.selectionDone ? "fechou" : "fecha"} ${this.clock(r.selectionClosesAt, true)}` : "30 min após o anúncio",
          done: !!r.selectionDone,
        },
        { label: "Lembrete aos guias", when: this.reminderLabel, done: !!r.dayBeforeDone },
        { label: "Desistências bloqueadas", when: this.clock(new Date(start - 30 * 60000), true), done: this.now >= start - 30 * 60000 },
        { label: "Início", when: this.clock(r.startAt, true), done: this.started },
      ];
    },
    announceNote() {
      const r = this.request;
      if (r.sharedAt) return `Enviado ${stamp(r.sharedAt)}`;
      if (r.publishAt) return `Agendado ${stamp(r.publishAt)}`;
      return r.status === "draft" ? "Enviado ao publicar" : "Não enviado";
    },
    reminderLabel() {
      const [y, m, d] = this.request.date.split("-").map(Number);
      const day = new Date(Date.UTC(y, m - 1, d - 1));
      return `${["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"][day.getUTCDay()]} 17:00`;
    },
    message() {
      return newTourMessage(this.request);
    },
    shownLog() {
      return this.showAllLog ? this.log : this.log.slice(0, 5);
    },
  },
  watch: {
    // Nothing to choose any more: leave "Selecionar vários"
    canEditTeam(on) {
      if (!on) this.toggleSelecting(false);
    },
  },
  mounted() {
    const id = this.$route.params.id;
    this.timer = setInterval(() => (this.now = Date.now()), 30000);
    this.unsubscribe = watchTourRequest(
      id,
      (data) => this.apply(data),
      (error) => {
        this.loading = false;
        this.$buefy.toast.open({ message: error.message, type: "is-danger", position: "is-bottom" });
      }
    );
  },
  beforeDestroy() {
    clearInterval(this.timer);
    if (this.unsubscribe) this.unsubscribe();
  },
  methods: {
    plural,
    whenLabel,
    stamp,
    waLink,
    ratingLabel,
    isSuggested(r) {
      return ["six", "any"].includes(r.suggestion);
    },
    toggleSelecting(on = !this.selecting) {
      this.selecting = on;
      if (!on) this.selected = [];
    },
    // "Tem um tour às 10:00" (another tour less than 1h30 away; may not make it)
    conflictText(r) {
      const list = r.conflicts || [];
      if (!list.length) return "";
      const at = (t) => (t.date === this.request.date ? t.startTime : `${dayLabel(t.date)} ${t.startTime}`);
      return `Tem um tour às ${list.map(at).join(" e às ")}`;
    },
    attendanceOf(g) {
      const a = g.attendance;
      if (!a) return "ok";
      if (a.status === "no_show") return "no_show";
      return a.minutes > 30 ? "late45" : a.minutes > 15 ? "late20" : "late10";
    },
    setAttendance(g, value) {
      const option = this.attendanceOptions.find((o) => o.id === value);
      return this.run("attendance", () => markAttendance(this.request.id, g.guideId, option.status, option.minutes), `${g.guideName}: ${option.label.toLowerCase()}.`);
    },
    // Client contact, notes and history come from the backend (admin only)
    apply(data) {
      this.request = data.request || null;
      this.priv = data.private || {};
      this.log = data.log || [];
      this.responses = data.responses || [];
      this.loading = false;
    },
    async loadAdminData() {
      try {
        this.apply(await getAdminData(this.$route.params.id));
      } catch (error) {
        this.$buefy.toast.open({ message: error.message, type: "is-danger", position: "is-bottom" });
      }
    },
    actorLabel(actor = "") {
      if (actor.startsWith("admin:")) return "Admin";
      if (actor.startsWith("guide:")) return "Guia";
      return "Sistema";
    },
    async copy(text) {
      try {
        await navigator.clipboard.writeText(text);
        this.$buefy.toast.open({ message: "Copiado.", position: "is-bottom" });
      } catch (error) {
        this.$buefy.toast.open({ message: "Não foi possível copiar.", type: "is-danger", position: "is-bottom" });
      }
    },
    sendAgain() {
      return this.run("send", () => sendTourMessage(this.request.id), "Enviado para o grupo.");
    },
    // "09:00", or "Qua 09:00" with the day (Lisbon)
    clock(value, withDay = false) {
      if (!value) return "–";
      const date = new Date(value);
      const time = new Intl.DateTimeFormat("pt-PT", { timeZone: "Europe/Lisbon", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(date);
      if (!withDay) return time;
      const day = new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Lisbon" }).format(date);
      return `${["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"][new Date(`${day}T12:00:00Z`).getUTCDay()]} ${time}`;
    },
    afterWindow(r) {
      return this.request.selectionClosesAt && r.respondedAt && new Date(r.respondedAt) > new Date(this.request.selectionClosesAt);
    },
    reconfirmState(g) {
      const rc = g.reconfirm;
      if (rc && rc.status === "pending") return { label: "A reconfirmar…", cls: "is-warn" };
      if (rc && rc.status === "not_reconfirmed") return { label: "Não reconfirmou", cls: "is-bad" };
      if (g.isExternal) return { label: "Adicionado pelo admin", cls: "" };
      // [Confirmo] in Telegram (selection message or the 17:00 reminder)
      if (g.confirmedAt) return { label: "Confirmou", cls: "is-ok" };
      // A confirmation is only asked after a swap or a change to the tour
      if (g.confirmNeeded) return { label: g.swapFrom ? `Troca com ${g.swapFrom} · por confirmar` : "Por confirmar", cls: "is-warn" };
      return { label: "Aprovado", cls: "is-ok" };
    },
    async run(key, action, done) {
      this.busy = key;
      try {
        await action();
        if (done) this.$buefy.toast.open({ message: done, type: "is-success", position: "is-bottom" });
        await this.loadAdminData();
      } catch (error) {
        this.$buefy.toast.open({ message: error.message, type: "is-danger", duration: 8000, position: "is-bottom" });
      } finally {
        this.busy = "";
      }
    },
    assignNow(w) {
      return this.run("assign", () => assignGuide(this.request.id, w.guideId), `${w.guideName} aprovado.`);
    },
    approveAll() {
      return this.run("assign", () => approveSuggestion(this.request.id), "Sugestão aprovada.");
    },
    swap(g) {
      this.openLeaving({ guides: [g], swap: true, to: null, reason: "asked", other: "" });
    },
    pendingFor(g) {
      return this.pending.find((p) => p.guideId === g.guideId) || null;
    },
    seatsChange(g) {
      const p = this.pendingFor(g);
      return p && p.newSeats ? p : null;
    },
    isLeaving(g) {
      const p = this.pendingFor(g);
      return !!p && !p.newSeats;
    },
    pendingLabel(p) {
      if (p.newSeats) return `Tuk de ${p.newSeats} lugares (era ${p.seats})`;
      return p.to ? `Troca por ${p.to.guideName}` : "Vai sair";
    },
    openSeats(g) {
      this.seatsOf = { guide: g, seats: Number(g.seats) === 6 ? 4 : 6 };
      this.$nextTick(() => this.$refs.seatsOverlay && this.$refs.seatsOverlay.focus());
    },
    confirmSeats() {
      const { guide, seats } = this.seatsOf;
      this.pending = [
        ...this.pending.filter((p) => p.guideId !== guide.guideId),
        { guideId: guide.guideId, guideName: guide.guideName, seats: guide.seats, newSeats: seats },
      ];
      this.seatsOf = null;
    },
    undoPending(g) {
      this.pending = this.pending.filter((p) => p.guideId !== g.guideId);
    },
    removeSelected() {
      const guides = this.team.filter((g) => this.selected.includes(g.guideId) && !this.isLeaving(g));
      if (guides.length) this.openLeaving({ guides, to: null, reason: "fewer_tuks", other: "" });
    },
    // One request: the backend saves everything, then sends the messages once
    savePending() {
      const changes = this.pending.map((p) =>
        p.newSeats
          ? { guideId: p.guideId, seats: p.newSeats }
          : {
              guideId: p.guideId,
              to: p.to ? p.to.guideId : null,
              reason: p.reason,
              other: p.other,
              // the admin chose someone whose tuk doesn't match the spot
              ...(p.to && g6(p, p.to) ? { force: true } : {}),
            }
      );
      const n = changes.length;
      return this.run(
        "team",
        async () => {
          await saveTeamChanges(this.request.id, changes);
          this.pending = [];
        },
        `${n === 1 ? "Alteração guardada" : `${n} alterações guardadas`}. Guias e grupo avisados.`
      );
    },
    // Why this guide shouldn't take g's place (same rules guides see); admins can still choose them
    swapProblem(g, w) {
      if (this.conflictText(w)) return `${this.conflictText(w)}, a menos de 1h30 deste`;
      if (Number(w.seats) < Number(g.seats)) return `Tem tuk de ${w.seats} lugares (o lugar é de ${g.seats})`;
      return "";
    },
    removeGuide(g) {
      this.openLeaving({ guides: [g], to: null, reason: "fewer_tuks", other: "" });
    },
    // Focus the sheet so Esc closes it
    openLeaving(leaving) {
      this.leaving = leaving;
      this.$nextTick(() => this.$refs.reasonOverlay && this.$refs.reasonOverlay.focus());
    },
    // Adds the change(s) to the list; nothing is saved until "Guardar e avisar"
    confirmLeaving() {
      const { guides, to, swap, reason, other } = this.leaving;
      if (swap && !to) return;
      if (reason === "other" && other.length < 3) {
        this.$buefy.toast.open({ message: "Escreve o motivo.", type: "is-danger", position: "is-bottom" });
        return;
      }
      const ids = guides.map((g) => g.guideId);
      this.pending = [
        ...this.pending.filter((p) => !ids.includes(p.guideId)),
        ...guides.map((g) => ({
          guideId: g.guideId,
          guideName: g.guideName,
          seats: g.seats,
          to: swap ? { guideId: to.guideId, guideName: to.guideName, seats: to.seats } : null,
          reason,
          other,
        })),
      ];
      this.toggleSelecting(false);
      this.leaving = null;
    },
    addExternal() {
      if (this.external.name.length < 2) {
        this.$buefy.toast.open({ message: "Escreve o nome do guia.", type: "is-danger", position: "is-bottom" });
        return;
      }
      return this.run(
        "external",
        async () => {
          await addExternalGuide(this.request.id, this.external);
          this.external = { name: "", phone: "", seats: 4 };
          this.showExternal = false;
        },
        "Guia externo adicionado."
      );
    },
    async publish() {
      this.busy = "publish";
      try {
        const { telegram } = await publishTourRequest(this.request.id);
        if (telegram && !telegram.sent) this.$buefy.toast.open({ message: `Publicado, mas o Telegram falhou: ${telegram.error}`, type: "is-warning", duration: 8000, position: "is-bottom" });
        await this.loadAdminData();
      } catch (error) {
        this.$buefy.toast.open({ message: error.message, type: "is-danger", position: "is-bottom" });
      } finally {
        this.busy = "";
      }
    },
    cancel() {
      // Drafts (scheduled ones too) never reached the group: cancelling tells no one
      const draft = this.request.status === "draft";
      this.$buefy.dialog.confirm({
        title: "Cancelar este tour?",
        message: draft ? "Ainda não foi anunciado, por isso ninguém é avisado. Não dá para desfazer." : "O grupo e os guias da equipa são avisados no Telegram. Não dá para desfazer.",
        confirmText: "Cancelar tour",
        cancelText: "Voltar",
        type: "is-danger",
        onConfirm: () => this.run("cancel", () => cancelTourRequest(this.request.id), "Tour cancelado."),
      });
    },
  },
};
</script>

<style scoped lang="scss">
@import "./coreanos";

.co-page{
    margin: -1rem !important;
}

.back {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  margin-bottom: 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: $ink-sub;

  &:hover {
    color: $forest;
  }
}

.loading {
  position: relative;
  min-height: 12rem;
}

.empty {
  padding: 2rem;
  color: $ink-sub;
}

.small {
  font-size: 0.8125rem;
  color: $ink-sub;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
}

.link {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  font-size: 0.875rem;
  font-weight: 500;
  color: $forest;
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
}

.icon-btn {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  border: 1px solid $line;
  border-radius: 999px;
  background: #fff;
  color: $forest;
  cursor: pointer;

  &:hover {
    background: $soft;
  }
}

.tag {
  display: inline-flex;
  align-items: center;
  margin-left: 0.375rem;
  padding: 0.0625rem 0.5rem;
  border-radius: 999px;
  background: $soft;
  font-size: 0.6875rem;
  font-weight: 600;
  vertical-align: middle;
  color: $ink-sub;

  &.is-ext,
  &.is-test {
    background: $warn-bg;
    color: $warn-ink;
  }

  &.is-suggested {
    background: $lime;
    color: $forest;
  }
}

// ---------- Header ----------
.head {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.head-tags {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.375rem;

  .tag {
    margin: 0;
  }
}

.ref {
  font-size: 0.8125rem;
  color: $ink-faint;
}

.head-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;

  .icon-btn {
    width: 2.25rem;
    height: 2.25rem;
  }
}

.is-danger-item {
  color: #a32c1f;
}

::v-deep .dropdown-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9375rem;
}

.route {
  margin: 0.75rem 0 0;
  padding: 0;
  list-style: none;

  li {
    position: relative;
    display: flex;
    gap: 0.75rem;

    & + li {
      margin-top: 0.375rem;
    }

    &:first-child::after {
      content: "";
      position: absolute;
      top: 0.875rem;
      bottom: -0.5rem;
      left: 0.28rem;
      border-left: 1.5px dashed $line;
    }

    > span:last-child {
      display: flex;
      flex-direction: column;
      font-size: 0.9375rem;
      font-weight: 500;
      line-height: 1.3;
      color: $ink;
    }
  }

  small {
    font-size: 0.6875rem;
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: $ink-faint;
  }
}

.pin {
  flex-shrink: 0;
  width: 0.625rem;
  height: 0.625rem;
  margin-top: 0.3rem;
  border: 2px solid $forest;
  border-radius: 999px;
  background: #fff;

  &.is-end {
    background: $forest;
  }
}

.facts {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 2rem;
  margin: 0 0 1rem;
  padding: 0.875rem 0;
  border-top: 1px solid $line;
  border-bottom: 1px solid $line;

  dt {
    font-size: 0.75rem;
    color: $ink-faint;
  }

  dd {
    margin: 0;
    font-family: $display;
    font-weight: 600;
    color: $forest;

    small {
      font-weight: 400;
      color: $ink-sub;
    }
  }
}

// ---------- Next step ----------
.next {
  display: flex;
  flex-wrap: wrap;
      align-items: flex-start;
  gap: 0.75rem 1rem;
  margin-bottom: 1.5rem;
  padding: 1rem 1.25rem;
  border: 1px solid $line;
  border-radius: 16px;
  background: #fff;

  &.is-action {
    border-color: $forest;
    background: #f6f9f6;
  }

  &.is-info {
    background: #f6f9f6;
  }

  &.is-warn {
    border-color: $warn-bg;
    background: $warn-soft;

    .next-icon {
      color: $orange;
          transform: rotateZ(89deg);
    }
  }

  &.is-ok .next-icon {
    color: $forest-2;
  }

  &.is-muted {
    background: $soft;
  }

  .next-actions {
    display: flex;
    gap: 0.5rem;
    margin-left: auto;
  }

  @media (max-width: 600px) {
    .next-actions {
      flex-direction: column-reverse;
      width: 100%;
      margin-left: 0;
    }

    .co-btn {
      width: 100%;
    }
  }
}

.next-icon {
  flex-shrink: 0;
  color: $forest;
}

.next-text {
  flex: 1;
  min-width: 12rem;
}

.next-title {
  font-family: $display;
  font-size: 1rem;
  font-weight: 600;
  color: $forest;
}

.next-sub {
  font-size: 0.875rem;
  color: $ink-sub;
}

// ---------- Layout ----------
.co-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 20rem;
  gap: 1.5rem;
  align-items: start;

  @media (max-width: 960px) {
    grid-template-columns: minmax(0, 1fr);
  }
}

// ---------- People ----------
.people {
  overflow: visible;
}

.tabs-bar {
  display: flex;
  gap: 0.25rem;
  padding: 0.375rem 0.375rem 0;
  border-bottom: 1px solid $line;
  overflow-x: auto;
  scrollbar-width: none;

  button {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    gap: 0.375rem;
    padding: 0.75rem 0.875rem;
    border: 0;
    border-bottom: 2px solid transparent;
    background: none;
    font: inherit;
    font-size: 0.9375rem;
    font-weight: 500;
    color: $ink-sub;
    cursor: pointer;

    &.is-active {
      border-bottom-color: $forest;
      color: $forest;
    }

    @media (max-width: 600px) {
      flex: 1;
      justify-content: center;
      gap: 0.25rem;
      padding: 0.75rem 0.125rem;
      font-size: 0.8125rem;
      white-space: nowrap;
    }
  }

  .count {
    padding: 0 0.4rem;
    border-radius: 999px;
    background: $soft;
    font-size: 0.75rem;
    color: $forest;
  }
}

.panel-tools {
  display: flex;
  justify-content: flex-end;
  padding: 0.75rem 1.25rem 0;
}

.panel-note {
  padding: 0.875rem 1.25rem 0.25rem;
  font-size: 0.8125rem;
  color: $ink-sub;
}

.person {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 1.25rem;
  border-bottom: 1px solid $line;

  &.is-selected {
    background: #f6f9f6;
  }

  &.is-pending {
    background: $warn-soft;

    .person-name {
      text-decoration: line-through;
      text-decoration-color: rgba(107, 58, 0, 0.5);
    }
  }

  &.is-muted .person-name {
    color: $ink-sub;
  }

  @media (max-width: 600px) {
    flex-wrap: wrap;
    padding: 0.875rem 1rem;
  }
}

.person.is-changed {
  background: #f6f9f6;
}

.seg.is-big button {
  height: 2.75rem;
  font-size: 0.9375rem;
}

.mt-small {
  margin-top: 0.75rem;
}

.pick input {
  width: 1.125rem;
  height: 1.125rem;
  accent-color: $forest;
  cursor: pointer;
}

.person-main {
  flex: 1;
  min-width: 0;
}

.person-name {
  font-weight: 600;
  color: $forest;
}

.person-meta {
  font-size: 0.8125rem;
  color: $ink-sub;
}

.state {
  font-weight: 500;

  &.is-ok {
    color: $forest-2;
  }

  &.is-warn {
    color: $orange;
  }

  &.is-bad {
    color: #a32c1f;
  }
}

.person-warn {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-top: 0.125rem;
  font-size: 0.8125rem;
  color: $warn-ink;
}

.person-actions {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 0.5rem;
}

.pending-tag {
  padding: 0.125rem 0.625rem;
  border-radius: 999px;
  background: $warn-bg;
  font-size: 0.8125rem;
  font-weight: 600;
  color: $warn-ink;
}

.attendance select {
  height: 2.25rem;
  padding: 0 0.625rem;
  border: 1px solid $line;
  border-radius: 10px;
  background: #fff;
  font: inherit;
  font-size: 0.875rem;
  color: $forest;
  cursor: pointer;
}

.attendance.is-late10 select,
.attendance.is-late20 select,
.attendance.is-late45 select {
  border-color: $warn-bg;
  background: $warn-soft;
  color: $warn-ink;
}

.attendance.is-no_show select {
  border-color: #f0c9c2;
  background: #fbe4e1;
  color: #a32c1f;
}

.slot {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: calc(100% - 2.5rem);
  margin: 0.75rem 1.25rem 0;
  padding: 0.75rem 1rem;
  border: 1.5px dashed $line;
  border-radius: 12px;
  background: none;
  font: inherit;
  text-align: left;
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: $forest-2;
    background: #f6f9f6;
  }

  &:disabled {
    cursor: default;
  }

  @media (max-width: 600px) {
    width: calc(100% - 2rem);
    margin: 0.75rem 1rem 0;
  }
}

.slot-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: 999px;
  background: $soft;
  color: $forest;
}

.slot-text {
  display: flex;
  flex: 1;
  flex-direction: column;
  color: $forest;
}

.slot-go {
  color: $ink-faint;
}

.empty-state {
  padding: 2rem 1.25rem;
  text-align: center;
  color: $ink-faint;
}

.external {
  padding: 1rem 1.25rem 1.25rem;
}

.external-form {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(11rem, 1fr));
  gap: 0.75rem;
  padding: 1rem;
  border: 1px solid $line;
  border-radius: 12px;
}

.external-title,
.external-actions {
  grid-column: 1 / -1;
}

.external-title {
  font-weight: 600;
  color: $forest;
}

.external-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 1rem;
}

.field-ui {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-size: 0.8125rem;
  font-weight: 500;
  color: $ink-sub;

  em {
    font-style: normal;
    color: $ink-faint;
  }

  input {
    height: 2.5rem;
    padding: 0 0.75rem;
    border: 1px solid $line;
    border-radius: 10px;
    font: inherit;
    font-size: 1rem;
    color: $ink;

    &:focus {
      border-color: $forest;
      outline: 0;
    }
  }
}

.seg {
  display: flex;
  padding: 0.1875rem;
  border-radius: 10px;
  background: $soft;

  button {
    flex: 1;
    height: 2.125rem;
    border: 0;
    border-radius: 8px;
    background: none;
    font: inherit;
    font-size: 0.875rem;
    color: $ink-sub;
    cursor: pointer;

    &.is-on {
      background: #fff;
      color: $forest;
      font-weight: 600;
      box-shadow: 0 1px 3px rgba(10, 30, 25, 0.12);
    }
  }
}

.pending-bar {
  position: sticky;
  bottom: 0;
  z-index: 5;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem calc(0.75rem + env(safe-area-inset-bottom));
  border-top: 1px solid $line;
  border-radius: 0 0 16px 16px;
  background: #fff;
  box-shadow: 0 -6px 16px rgba(10, 30, 25, 0.08);
}

.pending-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem 1rem;
  font-size: 0.9375rem;
  color: $forest;
}

.pending-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
}

// ---------- Side ----------
.side {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.side-card {
  padding: 1rem 1.25rem;
}

.side-title {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  margin-bottom: 0.625rem;
  font-family: $display;
  font-size: 0.9375rem;
  font-weight: 600;
  color: $forest;

  span {
    font-size: 0.75rem;
    font-weight: 400;
    color: $ink-faint;
  }
}

.client {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.phone {
  font-family: $display;
  font-weight: 600;
  color: $forest;
}

.client-actions {
  display: flex;
  gap: 0.375rem;
}

.notes {
  margin-top: 0.75rem;
  padding: 0.625rem 0.75rem;
  border-radius: 10px;
  background: $soft;
  font-size: 0.875rem;
  white-space: pre-line;
  color: $ink;
}

.timeline {
  margin: 0;
  padding: 0;
  list-style: none;

  li {
    position: relative;
    display: grid;
    grid-template-columns: 1rem 1fr auto;
    gap: 0.5rem;
    align-items: baseline;
    padding-bottom: 0.625rem;
    font-size: 0.875rem;

    &:not(:last-child)::after {
      content: "";
      position: absolute;
      top: 0.9rem;
      bottom: 0;
      left: 0.28rem;
      border-left: 1.5px solid $line;
    }

    &.is-done {
      .tl-dot {
        border-color: $forest-2;
        background: $forest-2;
      }

      .tl-label {
        color: $ink-sub;
      }
    }
  }
}

.tl-dot {
  width: 0.625rem;
  height: 0.625rem;
  border: 2px solid $line;
  border-radius: 999px;
  background: #fff;
}

.tl-label {
  color: $forest;
}

.tl-when {
  font-size: 0.8125rem;
  white-space: nowrap;
  color: $ink-faint;
}

.announce {
  summary {
    display: flex;
    flex-direction: column;
    cursor: pointer;
    list-style: none;

    &::-webkit-details-marker {
      display: none;
    }

    .side-title {
      margin-bottom: 0;

      &::after {
        content: "▾";
        margin-left: auto;
        color: $ink-faint;
      }
    }
  }

  &[open] .side-title::after {
    content: "▴";
  }

  .co-message {
    margin: 0.75rem 0 0.5rem;
    background: $soft;
  }
}

.history {
  margin: 0;
  padding: 0;
  list-style: none;

  li {
    padding: 0.5rem 0;
    border-top: 1px solid $line;

    &:first-child {
      border-top: 0;
      padding-top: 0;
    }
  }

  .entry {
    font-size: 0.875rem;
    color: $ink;
  }
}

// ---------- Remove / swap sheet ----------
.sheet-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(10, 30, 25, 0.55);
  outline: 0;

  @media (max-width: 768px) {
    align-items: flex-end;
    padding: 0;
  }
}

.sheet {
  width: 100%;
  max-width: 32rem;
  max-height: calc(100vh - 2rem);
  overflow-y: auto;
  padding: 1.5rem;
  background: #fff;

  textarea {
    width: 100%;
    margin-top: 0.5rem;
    padding: 0.75rem;
    border: 1px solid $line;
    border-radius: 10px;
    font: inherit;
    font-size: 1rem;
  }

  @media (max-width: 768px) {
    max-width: none;
    max-height: 88vh;
    border-radius: 20px 20px 0 0;
    padding-bottom: calc(1.5rem + env(safe-area-inset-bottom));
  }
}

.sheet-title {
  font-family: $display;
  font-size: 1.25rem;
  font-weight: 600;
  color: $forest;
}

.sheet-q {
  margin: 1.25rem 0 0.5rem;
  font-weight: 600;
  color: $forest;
}

.options {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.option {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  padding: 0.75rem 1rem;
  border: 1px solid $line;
  border-radius: 12px;
  background: #fff;
  font: inherit;
  text-align: left;
  cursor: pointer;

  &.is-on {
    border-color: $forest;
    box-shadow: inset 0 0 0 1px $forest;
    background: #f6f9f6;
  }

  &.is-radio input {
    position: absolute;
    opacity: 0;
  }
}

.option-name {
  font-weight: 600;
  color: $forest;
}

.option-warn {
  font-size: 0.8125rem;
  color: $warn-ink;
}

.sheet-note {
  display: flex;
  gap: 0.5rem;
  margin-top: 1.25rem;
  font-size: 0.8125rem;
  color: $ink-sub;
}

.sheet-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1rem;

  @media (max-width: 600px) {
    .co-btn {
      flex: 1;
    }
  }
}
</style>
